import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { findByTitulo, findByCategoria, findComunicadoByUsuario } from "./api-comunicado";
import { findAll } from "../ComunicadoCategoria/api-comunicado-categoria";
import { findByPerfil } from "../../Usuario/api-usuario-perfil";
import { ADMIN, COORDENADOR, PROFESSOR, SECRETARIA } from "../../Usuario/Perfil";
import TablePaginationActions from "../../../shared/components/Table/Actions";
import { dateTimeFormat } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";

const TITULO_KEY = "titulo";
const CATEGORIA_KEY = "categoria";
const USUARIO_KEY = "usuario";
const PERFIS_BUSCA = [ADMIN, COORDENADOR, PROFESSOR, SECRETARIA];

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

export default function ComunicadoFindDialog({
  openSearchComunicado,
  setOpenSearchComunicado,
  setComunicado,
}) {
  const [comunicados, setComunicados] = useState(EMPTY_PAGE);
  const [por, setPor] = useState(TITULO_KEY);
  const [busca, setBusca] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [listUP, setListUP] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsuarioPerfil, setIsLoadingUsuarioPerfil] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    findAll().then((data) => {
      if (Array.isArray(data)) {
        setCategorias(data);
      }
    });
  }, []);

  const loadUsuariosPerfil = () => {
    setIsLoadingUsuarioPerfil(true);
    Promise.all(PERFIS_BUSCA.map((perfil) => findByPerfil(perfil)))
      .then((results) => {
        const erro = results.find((data) => data?.error);
        if (erro) {
          errorMessage(erro.message);
        }
        setListUP(results.filter(Array.isArray).flat());
      })
      .finally(() => setIsLoadingUsuarioPerfil(false));
  };

  const handlePorChange = (event) => {
    const value = event.target.value;
    setPor(value);
    setBusca("");
    if (value === USUARIO_KEY && listUP.length === 0) {
      loadUsuariosPerfil();
    }
  };

  const fetchData = (newPage) => {
    let request;
    if (por === TITULO_KEY) {
      request = findByTitulo(busca, newPage);
    } else if (por === CATEGORIA_KEY) {
      request = findByCategoria(busca, newPage);
    } else {
      const usuarioPerfil = listUP.find((up) => up.codigo === busca);
      request = findComunicadoByUsuario(usuarioPerfil.usuario.id, newPage);
    }

    setIsLoading(true);
    request
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setComunicados(data);
          setHasSearched(true);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!busca) {
      errorToast(
        por === TITULO_KEY ? "Informe o título." : por === CATEGORIA_KEY ? "Selecione a categoria." : "Selecione o usuário.",
      );
      return;
    }
    fetchData(0);
  };

  const handleClose = () => {
    setOpenSearchComunicado(false);
  };

  const handleSelected = (comunicado) => {
    setComunicado(comunicado);
    handleClose();
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <Box py={4}>
          <Loading description="Carregando Comunicados..." />
        </Box>
      );
    }
    if (!hasSearched) return null;
    if (comunicados.content.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          Nenhum comunicado encontrado.
        </Typography>
      );
    }
    return (
      <>
        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" aria-label="Resultado da busca de comunicados">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                <TableCell>Título</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Criado em</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comunicados.content.map((comunicado) => (
                <TableRow
                  key={comunicado.codigo}
                  hover
                  onClick={() => handleSelected(comunicado)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{comunicado.titulo}</TableCell>
                  <TableCell>{comunicado.categoria?.descricao ?? ""}</TableCell>
                  <TableCell>{comunicado.usuario?.nome ?? ""}</TableCell>
                  <TableCell>{dateTimeFormat(comunicado.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          rowsPerPage={comunicados.size || 0}
          component="div"
          count={comunicados.totalElements}
          page={comunicados.number}
          onPageChange={(event, newPage) => fetchData(newPage)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? `${count} registros` : `mais de ${to}`}`
          }
          ActionsComponent={TablePaginationActions}
        />
      </>
    );
  };

  return (
    <Dialog
      open={openSearchComunicado}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="comunicado-find-dialog-title"
    >
      <DialogTitle id="comunicado-find-dialog-title">Procurar Comunicados</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <form onSubmit={clickSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <RadioGroup row name="por" value={por} onChange={handlePorChange}>
                  <FormControlLabel value={TITULO_KEY} control={<Radio />} label="Título" />
                  <FormControlLabel value={CATEGORIA_KEY} control={<Radio />} label="Categoria" />
                  <FormControlLabel value={USUARIO_KEY} control={<Radio />} label="Usuário" />
                </RadioGroup>
              </FormControl>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Box flexGrow={1}>
                  {por === TITULO_KEY && (
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      label="Título"
                      value={busca}
                      onChange={(event) => setBusca(event.target.value)}
                    />
                  )}
                  {por === CATEGORIA_KEY && (
                    <FormControl fullWidth size="small">
                      <InputLabel id="busca-categoria-label">Categoria</InputLabel>
                      <Select
                        labelId="busca-categoria-label"
                        label="Categoria"
                        value={busca}
                        onChange={(event) => setBusca(event.target.value)}
                      >
                        {categorias.map((categoria) => (
                          <MenuItem key={categoria.codigo} value={categoria.codigo}>
                            {categoria.descricao}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {por === USUARIO_KEY &&
                    (isLoadingUsuarioPerfil ? (
                      <Loading size={24} description="Carregando Usuários..." />
                    ) : (
                      <FormControl fullWidth size="small">
                        <InputLabel id="busca-usuario-label">Usuário</InputLabel>
                        <Select
                          labelId="busca-usuario-label"
                          label="Usuário"
                          value={busca}
                          onChange={(event) => setBusca(event.target.value)}
                        >
                          {listUP.map((usuarioPerfil) => (
                            <MenuItem key={usuarioPerfil.codigo} value={usuarioPerfil.codigo}>
                              {usuarioPerfil.usuario.nome} - {usuarioPerfil.perfil}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ))}
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchIcon />}
                  disabled={isLoading || isLoadingUsuarioPerfil}
                >
                  Buscar
                </Button>
              </Stack>
            </Stack>
          </form>

          {renderResults()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
